import { FC, MouseEvent, ReactNode, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalProps } from 'reactstrap';
import SvgRender from '@/library/components/general/SvgRender';
import styled from '@emotion/styled';
import variables from '@/library/assets/scss/abstracts/_exports.module.scss';
import useSvgIcon from '@/library/utils/hooks/useSvgIcon';

export interface DangerousActionModalBaseProps extends ModalProps {
  type?: string;
  header?: ReactNode;
  icon?: string | null;
  children?: ReactNode;
  svgColor?: string;
  svgStyle?: {
    transform?: string;
    width: number;
    height: number;
  };
  acceptButtonText?: string;
  cancelButtonText?: string;
  acceptButtonBackground?: string;
  cancelButtonBackground?: string;
  disabled?: boolean;
}

type DangerousActionModalProps =
  | (DangerousActionModalBaseProps & {
      onAccept: (event: MouseEvent<HTMLDivElement>) => void;
      onReject: (event: MouseEvent<HTMLDivElement>) => void;
      onCancel?: never;
    })
  | (DangerousActionModalBaseProps & {
      onAccept: (event: MouseEvent<HTMLDivElement>) => void;
      onCancel: (event: MouseEvent<HTMLDivElement>) => void;
      onReject?: never;
    })
  | (DangerousActionModalBaseProps & {
      onAcknowledge: (event: MouseEvent<HTMLDivElement>) => void;
      onAccept?: never;
      onReject?: never;
      onCancel?: never;
    });

const DangerousActionModal: FC<DangerousActionModalProps> = ({
  type = 'delete',
  icon,
  header,
  children,
  svgColor = 'red',
  svgStyle = { width: 80, height: 80 },
  onAcknowledge,
  onAccept,
  onCancel,
  onReject,
  acceptButtonText = 'DELETE',
  cancelButtonText = 'NO',
  acceptButtonBackground = 'red',
  cancelButtonBackground = 'timberWolf',
  disabled,
  ...rest
}) => {
  const svgUrl = useSvgIcon(type, icon);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onAcceptHandler = async (event: MouseEvent<HTMLDivElement>) => {
    if (disabled || isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (onAccept) await onAccept(event);

      if (!onReject) {
        setIsSubmitting(false);

        if (onCancel) return onCancel(event);
      }

      setIsSubmitting(false);
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      backdrop={false}
      centered
      {...rest}
      toggle={() => null}
      contentClassName="align-items-center bg-transparent"
    >
      <ModalBody className="p-0 d-flex flex-column align-items-center">
        <ModalContainer className="d-flex align-items-center justify-content-center flex-column max-w-416 min-w-416 py-4">
          {icon === null ? null : svgUrl ? (
            <div className="mb-4 d-flex align-items-center justify-content-center">
              <SvgRender src={svgUrl} style={svgStyle} className={`text-${svgColor}`} />
            </div>
          ) : null}
          <div className="fs-28 mb-2 lh-1">{header}</div>
          <div className="fs-18 fw-light text-center px-3 max-w-100p overflow-hidden">
            {children}
          </div>
        </ModalContainer>
      </ModalBody>
      <ModalFooter className="pt-0 pb-0 px-4 bg-transparent justify-content-center max-w-416 w-100p">
        {onAcknowledge ? (
          <ModalAcknowledgeActionConteiner>
            <ModalAction
              role="button"
              onClick={onAcknowledge}
              disabled={disabled || isSubmitting}
              color={'timberWolf'}
              className="fs-18 fw-bold text-davys-gray"
            >
              OK
            </ModalAction>
          </ModalAcknowledgeActionConteiner>
        ) : (
          <ModalActionsConteiner>
            <ModalAction
              role="button"
              onClick={disabled || isSubmitting || !rest.isOpen ? undefined : onReject || onCancel}
              disabled={disabled || isSubmitting}
              color={cancelButtonBackground}
            >
              {cancelButtonText}
            </ModalAction>
            <ModalAction
              role="button"
              onClick={disabled || isSubmitting || !rest.isOpen ? undefined : onAcceptHandler}
              disabled={disabled || isSubmitting}
              color={acceptButtonBackground}
            >
              {acceptButtonText}
            </ModalAction>
          </ModalActionsConteiner>
        )}
      </ModalFooter>
    </Modal>
  );
};

const ModalAcknowledgeActionConteiner = styled.div`
  box-shadow: 3px 8px 20px 0 rgba(0, 0, 0, 0.25);
  margin: 0;
  position: absolute;
  width: ${variables.size80};
  height: ${variables.size80};
  z-index: 1;
  border-radius: 100%;
  right: ${variables.size88};
  bottom: -${variables.size40};

  > div {
    border: none !important;
    width: 100%;
    height: 100%;
    border-radius: 100%;
  }
`;

export const ModalActionsConteiner = styled.div`
  box-shadow: 3px 8px 20px 0 rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  z-index: 1;
  margin: 0;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  overflow: hidden;
`;

export const ModalAction = styled.div`
  flex: 1;
  height: ${variables.size64};
  font-size: ${variables.size16};
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  cursor: ${({ disabled }: { disabled?: boolean }) => (disabled ? 'none' : 'pointer')};
  opacity: ${({ disabled }: { disabled?: boolean }) => (disabled ? 0.5 : 1)};
  transition: all 100ms ease-in-out;

  &:first-child {
    &:hover {
      color: white;
      background: ${({ color }: { color?: string }) => (color ? variables[color] : '')};
    }
  }

  &:last-child,
  &:nth-child(2) {
    border-left: 1px solid ${variables.platinum};

    &:hover {
      color: white;
      background: ${({ color }: { color?: string }) => (color ? variables[color] : '')};
    }
  }
`;

export const ModalContainer = styled.div`
  color: white;
  background: rgba(128, 128, 128, 0.7);
  border-radius: 5px 5px 3px 3px;
  box-shadow: 3px 8px 20px 0 rgba(0, 0, 0, 0.25);
  min-height: 25rem;
  backdrop-filter: blur(10px);
`;

export default DangerousActionModal;
